import { AppBar, Toolbar, Stack, Button, Typography, Box } from "@mui/material";
import { createContext, JSX, useContext, useEffect, useMemo, useState } from "react";
import { Icons } from "../image/Icons";
import { getPageTitles, headerTitleParts } from "../util/usePageTitle";
import { Clock } from "./Clock";
import { LoginSelect } from "./LoginSelect";
import { PageContext } from "../pages/ConsolePages/PageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthedApi } from "../api/Api";
import { VoidAction } from "../util/func";
import { HomeButton, ReloadButton } from "./FunctionalButton";
import { useYesNoDialog } from "./dialog/YesNoDialog";
import { useDeviceClass, ui } from "../util/responsive";

type PageHeaderSlotConfig = {
  priority?: number;
  minWidth?: number | string;
  align?: "start" | "center" | "end";
};

type PageHeaderProps = {
  left?: JSX.Element;
  center?: JSX.Element;
  right?: JSX.Element;
  leftConfig?: PageHeaderSlotConfig;
  centerConfig?: PageHeaderSlotConfig;
  rightConfig?: PageHeaderSlotConfig;
};

const pageSlotConfigtoFlex = (cfg?: PageHeaderSlotConfig, present?: boolean) => {
  const priority = cfg?.priority ?? (present ? 50 : 0);

  return {
    flexGrow: priority,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: cfg?.minWidth ?? 0, // ← 強制的に確保
  };
};
const alignToFlex = (align?: PageHeaderSlotConfig["align"]) => align === "center" ? "center" : align === "end" ? "flex-end" : "flex-start";

export default function PageHeader({ left, center, right, leftConfig, centerConfig, rightConfig }: PageHeaderProps) {
  const d = useDeviceClass();
  const lf = pageSlotConfigtoFlex(leftConfig, !!left);
  const cf = pageSlotConfigtoFlex(
    // 中央は既定で優先度高め（例: 80）
    { priority: 80, ...centerConfig },
    !!center
  );
  const rf = pageSlotConfigtoFlex(rightConfig, !!right);

  return (
    <AppBar position="sticky" sx={{ top: 0 }}>
      <Toolbar disableGutters sx={{ minHeight: ui.header.height[d] }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" height={ui.header.height[d]} overflow="hidden" gap={0.5}>

          <Stack key="left" direction="row" alignItems="center" justifyContent={alignToFlex(leftConfig?.align ?? "start")} overflow="hidden" minWidth={0} sx={lf} height="100%">
            <Box minWidth={0} maxWidth="100%" overflow="hidden" height="100%">
              {left}
            </Box>
          </Stack>

          <Stack key="center" direction="row" alignItems="center" justifyContent={alignToFlex(centerConfig?.align ?? "center")} overflow="hidden" minWidth={0} sx={cf} height="100%">
            <Box minWidth={0} maxWidth="100%" overflow="hidden">
              {center}
            </Box>
          </Stack>

          <Stack key="right" direction="row" alignItems="center" justifyContent={alignToFlex(rightConfig?.align ?? "end")} overflow="hidden" minWidth={0} sx={rf} height="100%">
            <Box minWidth={0} maxWidth="100%" overflow="hidden">
              {right}
            </Box>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

function SimpleHeader({ apiContext, headerTitle }: { apiContext: PageContext, headerTitle: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [returnDialog, showReturnDialog] = useYesNoDialog();
  const d = useDeviceClass();

  const back = () => {
    const idx = Number(window.history.state?.idx ?? 0);
    if (idx > 0) {
      // 前のページが同一オリジン
      navigate(-1);
    } else {
      // 遷移履歴がなければトップページに
      navigate("/console/");
    }
  };

  const tryBack = () => {
    const path = location.pathname;
    if (path.endsWith("/edit/") || path.endsWith("/new/")) {
      showReturnDialog({
        title: "前のページに戻りますか？",
        text: "現在入力中の情報はすべて失われます。",
        onConfirm: back,
      });
    } else if (path.endsWith("/process/")) {
      showReturnDialog({
        title: "前のページに戻りますか？",
        text: "現在作業中の情報はすべて失われます。",
        onConfirm: back,
      });
    }  else {
      back();
    }
  };

  const canPageBack = () => {
    let parentPath = location.pathname;
    if (parentPath.length > 1 && parentPath.endsWith("/")) {
      parentPath = parentPath.slice(0, -1);
    }
    return parentPath.lastIndexOf("/") > 0;
  }

  return (
    <PageHeader 
      left={
        <Stack direction="row" justifyItems="center" alignItems="center" overflow={"hidden"} minWidth={0} height="100%" pl={2}>
          <Stack display={canPageBack() ? "flex" : "none"} height="100%">
            <Button onClick={tryBack} sx={{ flex: 1, height: "100%", width: "100%"}}>
              <img width={ui.header.backIcon[d]} height={ui.header.backIcon[d]} src={Icons.BigArrowIcon} style={{ marginRight: "16px" }} />
            </Button>
          </Stack>
          <Typography fontSize={ui.header.titleFont[d]} fontWeight="bold" textOverflow="ellipsis" noWrap>{headerTitle}</Typography>
          <ReloadButton />
          {returnDialog}
        </Stack>
      } 
      right={
        <Stack direction="row" justifyItems="center" alignItems="center" gap={1} minWidth={0} pr={2}>
          <Clock />
          <LoginSelect pageContext={apiContext} />
          <HomeButton />
        </Stack>
      } 
      leftConfig={{ priority: 1, minWidth: ui.header.leftMinWidth[d] }}    // 左はそこそこ守る
      centerConfig={{ priority: 0, minWidth: 0 }}  // 中央は強めに守る
      rightConfig={{ priority: 1, minWidth: ui.header.rightMinWidth[d] }}   // 右は密度連動
    />
  );
}


type Ctx = {
  setTitleParts: React.Dispatch<React.SetStateAction<headerTitleParts>>;
  defaultTitleParts: headerTitleParts;
};
const SimpleHeaderTitleContext = createContext<Ctx | null>(null);

export function HeaderTitleProvider({apiContext, children}: { apiContext: PageContext, children: React.ReactNode }) {
  const location = useLocation();
  const [titleParts, setTitleParts] = useState<headerTitleParts>({});
  
  useEffect(() => {
    setTitleParts({});
  }, [location.pathname]);

  const defaultTitleParts = useMemo(
    () => getPageTitles(new URL(location.pathname, window.location.origin)),
    [location.pathname]
  );

  const headerTitle = `${titleParts.title ?? defaultTitleParts.title ?? ""}` + `${titleParts.prefix ?? defaultTitleParts.prefix ?? ""}` + `${titleParts.suffix ?? defaultTitleParts.suffix ?? ""}`;
  const value = useMemo(() => ({ setTitleParts, defaultTitleParts }), [defaultTitleParts]);

  return (
    <SimpleHeaderTitleContext.Provider value={value}>
      <SimpleHeader apiContext={apiContext} headerTitle={headerTitle} />
      {children}
    </SimpleHeaderTitleContext.Provider>
  );
}

export function useHeaderTitle() {
  const ctx = useContext(SimpleHeaderTitleContext);
  if (!ctx) throw new Error("useHeaderTitle must be used inside <HeaderTitleProvider>");
  return ctx;
}

export function useAutoHeaderTitle(compute: (defaults: headerTitleParts) => headerTitleParts, deps: React.DependencyList = []) {
  const { setTitleParts, defaultTitleParts } = useHeaderTitle();

  useEffect(() => {
    setTitleParts(compute(defaultTitleParts));
  }, [setTitleParts, defaultTitleParts, ...deps]);
}

export function LoginPageHeader({ api, update } : { api: AuthedApi | null, update: VoidAction }) {
  return <>
    <PageHeader right={
      <>
      {api !== null && <Stack direction="row" justifyItems="center" alignItems="center" gap={4}>
        <LoginSelect pageContext={{ api, update }} />
      </Stack>}
      </>}
    />
  </>
}